/**
 * VIP MEMBERSHIP CARD — the ★ entry gate, reskinned as an exclusive matte-black
 * casino card (Amex-Centurion energy). It is a DOM overlay (it needs a text
 * input), but it must read as a PHYSICAL premium card: matte-black/charcoal
 * surface, gold-foil accents, a gold EMV chip, a casino crest/monogram, a subtle
 * guilloché/holographic foil background, an EMBOSSED cardholder name (the Name
 * input is styled as embossed gold text), a faux card number + tier line, and the
 * casino wordmark. The Player/Croupier toggle + Enter read as integral card
 * controls, not generic web buttons.
 *
 * Behaviour is UNCHANGED from before: on submit it persists identity, unlocks
 * audio (the FIRST lobby gesture — autoplay policy), and sends the `join` intent.
 *
 * The chip/crest images are GUARDED: a missing SVG (`onError`) simply hides the
 * <img>, falling back to the pure-CSS foil chip / monogram so the card always
 * renders. Foil shimmer is CSS (animated background-position); no JS animation.
 */

import { useState } from 'react';
import type { Role } from '@blackjack/shared';
import { useGameStore } from '../state/store';
import { useSend } from '../net/RealtimeProvider';
import { saveIdentity, loadIdentity } from '../net/identity';
import { unlockAudio } from '../audio/sfx';
import { assetPaths } from '../theme/tokens';

/** Build a faux member number from the stable clientId (display only). */
function memberNumber(seed: string): string {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const n = (h >>> 0).toString().padStart(12, '0').slice(0, 12);
  return `${n.slice(0, 4)}  ${n.slice(4, 8)}  ${n.slice(8, 12)}`;
}

export function IdCard() {
  const send = useSend();
  const lastError = useGameStore((s) => s.lastError);
  const roster = useGameStore((s) => s.server.roster);

  const existing = loadIdentity();
  const [name, setName] = useState(existing?.name ?? '');
  const [role, setRole] = useState<Role>(existing?.role ?? 'player');
  const [chipOk, setChipOk] = useState(true);
  const [crestOk, setCrestOk] = useState(true);

  const croupierTaken = roster.some((r) => r.role === 'croupier');
  const canSubmit = name.trim().length >= 2 && !(role === 'croupier' && croupierTaken);
  const cardNo = memberNumber(existing?.clientId ?? (name || 'guest'));
  const tier = role === 'croupier' ? 'HOUSE · CROUPIER' : 'BLACK · MEMBER';

  const submit = () => {
    if (!canSubmit) return;
    // First lobby gesture → unlock audio (never on page load).
    unlockAudio();
    const identity = saveIdentity(name, role);
    const store = useGameStore.getState();
    store.setAudioUnlocked();
    store.setIdentity(identity);
    send({ type: 'join', name: identity.name, role: identity.role, clientId: identity.clientId });
    store.setJoined(true);
  };

  return (
    <div className="vip-backdrop">
      <div className={`vipcard ${role === 'croupier' ? 'vipcard--house' : ''}`}>
        {/* Guilloché / holographic foil background layers (pure CSS). */}
        <div className="vipcard__guilloche" aria-hidden="true" />
        <div className="vipcard__holo" aria-hidden="true" />

        {/* Top row: casino wordmark + crest/monogram. */}
        <header className="vipcard__top">
          <div className="vipcard__wordmark heading-gold">CHIP &amp; SHUFFLE</div>
          <div className="vipcard__crest">
            {crestOk ? (
              <img
                src={assetPaths.uiCrest}
                alt=""
                aria-hidden="true"
                onError={() => setCrestOk(false)}
              />
            ) : (
              <span className="vipcard__monogram heading-gold">C&amp;S</span>
            )}
          </div>
        </header>

        {/* Chip + tier line. */}
        <div className="vipcard__chiprow">
          {chipOk ? (
            <img
              className="vipcard__chip-img"
              src={assetPaths.uiChip}
              alt=""
              aria-hidden="true"
              onError={() => setChipOk(false)}
            />
          ) : (
            <div className="vipcard__chip" aria-hidden="true" />
          )}
          <span className="vipcard__tier numeric">{tier}</span>
        </div>

        {/* Embossed cardholder name (the input styled as engraved gold). */}
        <label className="vipcard__name-field">
          <span className="vipcard__caption">CARDHOLDER</span>
          <input
            className="vipcard__name-input"
            type="text"
            value={name}
            maxLength={24}
            placeholder="YOUR NAME"
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </label>

        {/* Faux member number + role select share ONE row so the card stays
            credit-card proportioned (the standalone role row made it too tall). */}
        <div className="vipcard__numrow">
          <span className="vipcard__number">{cardNo}</span>
          <div className="vipcard__roles" role="group" aria-label="Membership type">
            <button
              type="button"
              className={`vip-seg ${role === 'player' ? 'is-active' : ''}`}
              onClick={() => setRole('player')}
            >
              Player
            </button>
            <button
              type="button"
              className={`vip-seg ${role === 'croupier' ? 'is-active' : ''}`}
              disabled={croupierTaken && role !== 'croupier'}
              onClick={() => setRole('croupier')}
            >
              Croupier{croupierTaken ? ' · taken' : ''}
            </button>
          </div>
        </div>

        {role === 'croupier' && croupierTaken && (
          <p className="vipcard__warn">A croupier is already seated. Pick Player.</p>
        )}
        {lastError && <p className="vipcard__warn">{lastError.message}</p>}

        {/* Enter — the card's primary action, reads as an embossed gold key. */}
        <button type="button" className="vip-enter" disabled={!canSubmit} onClick={submit}>
          <span className="vip-enter__label">Enter the Casino</span>
        </button>

        <footer className="vipcard__foot">
          <span className="vipcard__valid">MEMBER SINCE {new Date().getFullYear()}</span>
          <span className="vipcard__valid">VALID AT ALL TABLES</span>
        </footer>
      </div>
    </div>
  );
}
